<template>
    <button type="button" class="stock-card" @click="$emit('waste', item)">
        <span class="stock-card__header">
            <span class="stock-card__image" :style="imageStyle" role="img" :aria-label="name"></span>
            <span class="stock-card__name">{{ name }}</span>
            <span class="stock-card__chevron" aria-hidden="true">
                <i class="i-andy-chevron-right i-andy-xs"></i>
            </span>
        </span>

        <span class="stock-card__body">
            <span class="stock-card__total">
                <span class="stock-card__total-label">{{ $t('phu_production.stock.title') }}</span>
                <span class="stock-card__total-value" :class="{ 'stock-card__total-value--empty': item.units <= 0 }">{{ item.units }}</span>
            </span>

            <span class="stock-card__breakdown">
                <span v-for="row in rows" :key="row.key" class="stock-card__row">
                    <i :class="row.iconClass" aria-hidden="true"></i>
                    <span class="stock-card__row-label">{{ row.label }}</span>
                    <span class="stock-card__row-value">{{ row.value }}</span>
                </span>
            </span>
        </span>
    </button>
</template>

<script>
export default {
    name: 'StockCard',
    props: {
        item: { type: Object, required: true },
        product: { type: Object, default: null }
    },
    computed: {
        name() {
            return this.product ? this.product.name : ''
        },
        imageStyle() {
            if (!this.product || !this.product.image) {
                return {}
            }
            return { backgroundImage: `url(${this.product.image})` }
        },
        rows() {
            return [
                { key: 'produced', iconClass: 'i-andy-add i-andy-s stock-card__icon stock-card__icon--add', label: this.$t('phu_production.stock.produced'), value: this.item.produced },
                { key: 'sales', iconClass: 'i-andy-less i-andy-s stock-card__icon stock-card__icon--less', label: this.$t('phu_production.stock.sales'), value: this.item.sales },
                { key: 'waste', iconClass: 'i-andy-less i-andy-s stock-card__icon stock-card__icon--less', label: this.$t('phu_production.stock.waste'), value: this.item.waste }
            ]
        }
    }
}
</script>

<style lang="scss" scoped>
@import '~@/components/domain/phu-production/styles/tokens';

.stock-card {
    @include display-flex();
    @include flex-direction(column);
    gap: $phu-gap;
    width: 100%;
    padding: 8px;
    border: 1px solid $neutro-dark;
    border-radius: $phu-radius-s;
    background-color: $phu-surface;
    box-shadow: 0 0 3px rgba(0, 0, 0, 0.02), 0 2px 2px rgba(0, 0, 0, 0.08);
    text-align: left;
    cursor: pointer;

    &__header {
        @include display-flex();
        @include align-items(center);
        gap: $phu-gap;
        min-width: 0;
    }

    &__image {
        flex-shrink: 0;
        width: 48px;
        height: 48px;
        border-radius: $phu-radius-s;
        background-position: center;
        background-size: contain;
        background-repeat: no-repeat;
    }

    &__name {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        font-family: $text-bold;
        font-size: 1.25rem;
        line-height: 24px;
        text-transform: uppercase;
        word-break: normal;
        overflow-wrap: normal;
        color: $phu-text;
    }

    &__chevron {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex-shrink: 0;
        padding: 8px;
        border-radius: $phu-radius-s;
        background-color: $phu-button;
        color: $phu-primary;
    }

    &__body {
        @include display-flex();
        gap: $phu-gap;
        align-items: stretch;
    }

    &__total {
        @include display-flex();
        @include flex-direction(column);
        @include align-items(center);
        @include justify-content(center);
        flex-shrink: 0;
        width: 82px;
        height: 82px;
        border: 1px solid $neutro-dark;
        border-radius: $phu-radius-s;
        background-color: $neutro-t80;
    }

    &__total-label {
        font-family: $text;
        font-size: 1rem;
        line-height: normal;
        text-transform: uppercase;
        color: $phu-text;
    }

    &__total-value {
        font-family: $text-bold;
        font-size: 3rem;
        line-height: 1;
        color: $phu-text;

        &--empty {
            color: $phu-error;
        }
    }

    &__breakdown {
        @include display-flex();
        @include flex-direction(column);
        flex: 1;
        min-width: 0;
        border: 1px solid $neutro-dark;
        border-radius: $phu-radius-s;
        background-color: $neutro-t80;
        overflow: hidden;
    }

    &__row {
        @include display-flex();
        @include align-items(center);
        gap: 4px;
        flex: 1;
        min-width: 0;
        padding: 0 8px;
        border-bottom: 1px solid $neutro-dark;

        &:last-child {
            border-bottom: none;
        }
    }

    &__icon--add {
        color: $phu-success;
    }

    &__icon--less {
        color: $phu-error;
    }

    &__row-label {
        flex: 1;
        min-width: 0;
        font-family: $text;
        font-size: 0.875rem;
        line-height: normal;
        text-transform: uppercase;
        @include text-ellipsis();
        color: $neutro-s50;
    }

    &__row-value {
        flex-shrink: 0;
        min-width: 28px;
        padding-left: 8px;
        border-left: 1px solid $neutro-dark;
        font-family: $text-bold;
        font-size: 1rem;
        line-height: normal;
        text-align: right;
        color: $phu-text;
    }
}
</style>
